import React from 'react';
import { Target, CheckCircle, Plus, Trash2 } from 'lucide-react';
import { useTranslation } from '../../../contexts/TranslationContext';
import { Lesson } from '../../../types';

interface LearningObjectivesSectionProps {
  formData: Partial<Lesson>;
  setFormData: React.Dispatch<React.SetStateAction<Partial<Lesson>>>;
  errors: Record<string, string>;
}

export const LearningObjectivesSection: React.FC<LearningObjectivesSectionProps> = ({
  formData,
  setFormData,
  errors
}) => {
  const { t } = useTranslation();
  const addObjective = () => {
    setFormData(prev => ({
      ...prev,
      objectives: [...(prev.objectives || []), '']
    }));
  };

  const updateObjective = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      objectives: prev.objectives?.map((obj, i) => i === index ? value : obj) || []
    }));
  };

  const removeObjective = (index: number) => {
    setFormData(prev => ({
      ...prev,
      objectives: prev.objectives?.filter((_, i) => i !== index) || []
    }));
  };

  const addSuccessCriteria = () => {
    setFormData(prev => ({
      ...prev,
      success_criteria: [...(prev.success_criteria || []), '']
    }));
  };

  const updateSuccessCriteria = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      success_criteria: prev.success_criteria?.map((criteria, i) => i === index ? value : criteria) || []
    }));
  };

  const removeSuccessCriteria = (index: number) => {
    setFormData(prev => ({
      ...prev,
      success_criteria: prev.success_criteria?.filter((_, i) => i !== index) || []
    }));
  };

  const addStandard = () => {
    setFormData(prev => ({
      ...prev,
      standards_alignment: [...(prev.standards_alignment || []), '']
    }));
  };

  const updateStandard = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      standards_alignment: prev.standards_alignment?.map((standard, i) => i === index ? value : standard) || []
    }));
  };

  const removeStandard = (index: number) => {
    setFormData(prev => ({
      ...prev,
      standards_alignment: prev.standards_alignment?.filter((_, i) => i !== index) || []
    }));
  };

  return (
    <>
      {/* Learning Objectives */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Target className="w-5 h-5 mr-2" />
          {t('learningObjectives.learningObjectives')} *
        </h2>
        
        <div className="space-y-3">
          {formData.objectives?.map((objective, index) => (
            <div key={index} className="flex items-center space-x-3">
              <input
                type="text"
                value={objective}
                onChange={(e) => updateObjective(index, e.target.value)}
                className="input-field flex-1"
                placeholder={`${t('learningObjectives.objective')} ${index + 1}`}
              />
              {formData.objectives && formData.objectives.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeObjective(index)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
          
          <button
            type="button"
            onClick={addObjective}
            className="btn-secondary flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>{t('learningObjectives.addObjective')}</span>
          </button>
        </div>
        {errors.objectives && <p className="text-red-600 text-sm mt-2">{errors.objectives}</p>}
      </div>

      {/* Success Criteria */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <CheckCircle className="w-5 h-5 mr-2" />
          {t('learningObjectives.successCriteria')}
        </h2>
        
        <div className="space-y-3">
          {formData.success_criteria?.map((criteria, index) => (
            <div key={index} className="flex items-center space-x-3">
              <input
                type="text"
                value={criteria}
                onChange={(e) => updateSuccessCriteria(index, e.target.value)}
                className="input-field flex-1"
                placeholder={`${t('learningObjectives.successCriteriaItem')} ${index + 1}`}
              />
              <button
                type="button"
                onClick={() => removeSuccessCriteria(index)}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          
          <button
            type="button"
            onClick={addSuccessCriteria}
            className="btn-secondary flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>{t('learningObjectives.addSuccessCriteria')}</span>
          </button>
        </div>
      </div>

      {/* Standards Alignment */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Target className="w-5 h-5 mr-2" />
          {t('learningObjectives.standardsAlignment')}
        </h2>
        
        <div className="space-y-3">
          {formData.standards_alignment?.map((standard, index) => (
            <div key={index} className="flex items-center space-x-3">
              <input
                type="text"
                value={standard}
                onChange={(e) => updateStandard(index, e.target.value)}
                className="input-field flex-1"
                placeholder={t('learningObjectives.standardPlaceholder').replace('{index}', (index + 1).toString())}
              />
              <button
                type="button"
                onClick={() => removeStandard(index)}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          
          <button
            type="button"
            onClick={addStandard}
            className="btn-secondary flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>{t('learningObjectives.addStandard')}</span>
          </button>
        </div>
      </div>
    </>
  );
};
